import { type CSSProperties, type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BadgeCheck, Briefcase, Check, Clock, Crown, Gavel, LockKeyhole, Minus, Plus, ShieldCheck, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { APP_URL, HOME_FAQ_QUESTIONS, HOME_FAQS, MARKETING_PLANS } from '@/data/marketingContent';
import { cn } from '@/lib/utils';
import { AnimatedNumber, EASE, Eyebrow, Reveal, SectionHeading, SpotlightCard } from './primitives';
import { ScreeningCalculator } from '@/components/tools/ScreeningCalculator';

function SmartLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  return href.startsWith('/') ? <Link to={href} className={className}>{children}</Link> : <a href={href} className={className}>{children}</a>;
}

/* ───────────────────────── Screening cost calculator ───────────────────────── */

export function ScreeningCostCalculator() {
  return (
    <section id="screening-calculator" className="relative scroll-mt-20 overflow-hidden bg-white py-24 lg:py-32" aria-labelledby="calculator-title">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading id="calculator-title" eyebrow="Screening cost calculator" title="How much recruiter time does first-pass screening cost your desk?" description="Move the sliders to match a typical month. The estimate updates as you go, and nothing you enter is stored." />
          <Reveal><Link to="/screening-cost-calculator" className="group inline-flex items-center gap-1.5 whitespace-nowrap font-semibold text-bv-blue">Open the full calculator <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Reveal>
        </div>
        <div className="mt-12"><ScreeningCalculator idPrefix="home-calc" /></div>
      </div>
    </section>
  );
}

/* ───────────────────────── Control ───────────────────────── */

const controls = [
  { icon: Gavel, title: 'Recruiters decide', body: 'Beatview prepares and scores. Your recruiter decides who moves forward and what is shared with a client.' },
  { icon: BadgeCheck, title: 'Your agency brand', body: 'Every plan includes a branded career page. Pro adds custom domain and custom email options.' },
  { icon: ShieldCheck, title: 'Consent and data', body: 'Capture consent at application and review Beatview’s published privacy and retention information.', href: '/privacy-policy', link: 'Read the policy' },
  { icon: LockKeyhole, title: 'Client-safe sharing', body: 'Send a password-protected candidate link and collect external notes without opening your workspace.' },
];

export function ControlSection() {
  return (
    <section className="bg-bv-blue-wash py-24 lg:py-32" aria-labelledby="control-title">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading id="control-title" eyebrow="Human in the loop" title="Your agency keeps control of every decision" description="The agent prepares the work. Your recruiters own candidate judgement, agency presentation, data handling and every client recommendation." />
          <Reveal><Link to="/security" className="group inline-flex items-center gap-1.5 whitespace-nowrap font-semibold text-bv-blue">How we handle security <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Reveal>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {controls.map(({ icon: Icon, title, body, href, link }, i) => (
            <Reveal key={title} delay={i * 0.06}>
              <SpotlightCard className="flex h-full flex-col p-6">
                <span className="bv-btn-gradient flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-bv-action"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-6 font-display text-xl font-semibold text-bv-ink">{title}</h3>
                <p className="mt-2 flex-1 text-[15px] leading-6 text-bv-ink2">{body}</p>
                {href && <Link to={href} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-bv-blue">{link}<ArrowRight className="h-3.5 w-3.5" /></Link>}
              </SpotlightCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Desk solutions ───────────────────────── */

const deskCards = [
  { icon: Briefcase, name: 'Permanent recruitment', body: 'Give every permanent-role application the same first review.', href: '/solutions/permanent-recruitment' },
  { icon: Clock, name: 'Contract & temp staffing', body: 'Keep fast-moving desks structured when applications arrive together.', href: '/solutions/contract-temp-staffing' },
  { icon: Crown, name: 'Executive search', body: 'Inspect the evidence behind a smaller, higher-stakes candidate set.', href: '/solutions/executive-search' },
  { icon: Users, name: 'RPO & embedded teams', body: 'Keep delivery consistent across recruiters, roles and client teams.', href: '/solutions/rpo-embedded-teams' },
];

export function DeskSolutions() {
  return (
    <section className="bg-white py-24 lg:py-32" aria-labelledby="desks-title">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
        <SectionHeading id="desks-title" align="center" eyebrow="Solutions" title="Built for the way agency desks actually work" description="Whether you fill one executive role a quarter or fifty contract roles a week, the agent runs the same structured first review." />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {deskCards.map(({ icon: Icon, name, body, href }, i) => (
            <Reveal key={name} delay={i * 0.06}>
              <Link to={href} className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-bv-blue-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-bv-window focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue">
                <span aria-hidden className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-[hsl(var(--hero-highlight-start))] to-[hsl(var(--hero-highlight-end))] transition-transform duration-500 group-hover:scale-x-100" />
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue transition-colors group-hover:bg-bv-ink group-hover:text-white"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-6 font-display text-xl font-semibold text-bv-ink">{name}</h3>
                <p className="mt-2 flex-1 text-[15px] leading-6 text-bv-ink2">{body}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-bv-blue">See the workflow <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Comparison ───────────────────────── */

const comparisonRows = [
  ['Reads every application', 'A recruiter reads each one', 'Stores applications', 'Yes'],
  ['Scores against role criteria', 'Manual review', 'Varies by ATS', 'Yes, against configured criteria'],
  ['Runs structured interviews', 'Recruiter-led', 'Usually separate', 'Yes, timed video questions'],
  ['Shows evidence for each score', 'Reviewer notes', 'Varies by ATS', 'Yes'],
  ['Recruiter keeps final decision', 'Yes', 'Yes', 'Yes'],
  ['Client-safe sharing', 'Manual documents', 'Varies by ATS', 'Password-protected link'],
  ['Role setup', 'Manual process', 'Configuration required', 'Configure criteria and stages'],
  ['Commercial model', 'Staff time', 'Vendor dependent', 'Free, Pro, or Enterprise'],
];

export function ComparisonSection() {
  return (
    <section className="relative overflow-hidden bg-bv-ink py-24 text-white lg:py-32" aria-labelledby="comparison-title">
      <div aria-hidden className="bv-grid-dark absolute inset-0" />
      <div aria-hidden className="absolute left-1/2 top-0 h-[360px] w-[760px] -translate-x-1/2 rounded-full bg-[hsl(var(--blue)/0.2)] blur-[120px]" />
      <div className="relative mx-auto max-w-[1240px] px-5 lg:px-8">
        <SectionHeading tone="dark" id="comparison-title" eyebrow="Comparison" title="Beatview compared with manual screening and a standard ATS" />
        <Reveal delay={0.1}>
          <div className="mt-12 overflow-x-auto rounded-3xl border border-white/10 bg-white/[0.03]" tabIndex={0} aria-label="Comparison table, scrolls horizontally on small screens">
            <table className="w-full min-w-[820px] border-collapse text-left text-[15px]">
              <caption className="sr-only">Beatview compared with manual screening and a standard applicant tracking system</caption>
              <thead>
                <tr>
                  <th scope="col" className="px-6 py-5 text-sm font-semibold text-white/50">Capability</th>
                  <th scope="col" className="px-6 py-5 text-sm font-semibold text-white/70">Manual screening</th>
                  <th scope="col" className="px-6 py-5 text-sm font-semibold text-white/70">Standard ATS</th>
                  <th scope="col" className="relative bg-white/[0.07] px-6 py-5"><span aria-hidden className="bv-btn-gradient absolute inset-x-0 top-0 h-[3px]" /><span className="inline-flex items-center gap-2 font-display text-base font-semibold text-white">Beatview</span></th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map(([capability, manual, ats, beatview]) => (
                  <tr key={capability} className="border-t border-white/[0.08]">
                    <th scope="row" className="px-6 py-4 font-semibold text-white">{capability}</th>
                    {[manual, ats].map((cell, i) => (
                      <td key={i} className="px-6 py-4 text-white/60">
                        <span className="inline-flex items-center gap-2">{cell === 'Yes' ? <Check className="h-4 w-4 text-white/50" /> : <Minus className="h-4 w-4 text-white/25" />}{cell}</span>
                      </td>
                    ))}
                    <td className="bg-white/[0.07] px-6 py-4 font-medium text-white">
                      <span className="inline-flex items-center gap-2"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bv-success"><Check className="h-3 w-3" strokeWidth={3} /></span>{beatview}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <Reveal><p className="mt-8 text-[17px] text-white/70">Beatview runs alongside your ATS rather than replacing it — most ATS platforms are supported through Zapier, and native integrations are enabled on request. <Link to="/integrations" className="group inline-flex items-center gap-1 font-semibold text-bv-blue-bright">See integrations <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></p></Reveal>
      </div>
    </section>
  );
}

/* ───────────────────────── Pricing ───────────────────────── */

export function PricingSection() {
  const [annual, setAnnual] = useState(true);
  return (
    <section className="bg-bv-base py-24 lg:py-32" aria-labelledby="pricing-title">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
        <div className="flex flex-col items-center text-center">
          <SectionHeading id="pricing-title" align="center" eyebrow="Pricing" title="Start with one role. Add more when it pays for itself." description="Start free, add AI interviews with Pro, or discuss unlimited active roles and integrations." />
          <Reveal delay={0.15}>
            <div role="group" aria-label="Billing period" className="mt-8 inline-flex rounded-full border border-bv-blue-line bg-white p-1 shadow-bv-card">
              {([['Monthly', false], ['Annual', true]] as const).map(([label, value]) => {
                const selected = annual === value;
                return (
                  <button key={label} type="button" aria-pressed={selected} onClick={() => setAnnual(value)} className="relative rounded-full px-5 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue">
                    {selected && <motion.span layoutId="billing-pill" className="absolute inset-0 rounded-full bg-bv-ink" transition={{ type: 'spring', stiffness: 420, damping: 32 }} />}
                    <span className={cn('relative inline-flex items-center gap-1.5', selected ? 'text-white' : 'text-bv-ink2')}>
                      {label}
                      {value && <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold', selected ? 'bg-bv-success text-white' : 'bg-bv-success-soft text-bv-success')}>Save 18%</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3 lg:items-stretch">
          {MARKETING_PLANS.map((plan, i) => {
            const price = annual ? plan.annualMonthly : plan.monthly;
            const featured = !!plan.featured;
            return (
              <Reveal key={plan.name} delay={i * 0.08} className={cn(featured && 'order-first lg:order-none')}>
                <article className={cn('relative flex h-full flex-col overflow-hidden rounded-3xl p-7 sm:p-8', featured ? 'bg-bv-ink text-white shadow-[0_40px_90px_-40px_hsl(var(--navy)/0.7)] ring-1 ring-white/10' : 'border border-bv-blue-line bg-white')}>
                  {featured && <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[hsl(var(--hero-highlight-start)/0.35)] blur-[80px]" />}
                  <div className="relative flex items-center justify-between gap-3">
                    <h3 className={cn('font-display text-2xl font-semibold', featured ? 'text-white' : 'text-bv-ink')}>{plan.name}</h3>
                    {featured && <span className="bv-btn-gradient rounded-full px-3 py-1 text-[12px] font-semibold text-white">Recommended</span>}
                  </div>
                  <p className={cn('relative mt-3 min-h-12 text-[15px] leading-6', featured ? 'text-white/70' : 'text-bv-ink2')}>{plan.description}</p>
                  <div className="relative mt-6 min-h-[88px]">
                    {price === null
                      ? <p className="font-display text-4xl font-semibold">Let’s talk</p>
                      : <p className="flex items-baseline gap-1"><span className="font-display text-6xl font-semibold tracking-[-0.04em] tabular-nums"><AnimatedNumber value={price} format={v => `$${Math.round(v)}`} /></span><span className={cn('text-[15px]', featured ? 'text-white/60' : 'text-bv-ink2')}>/ month</span></p>}
                    {annual && plan.annualTotal && <p className={cn('mt-1 text-[13px]', featured ? 'text-white/60' : 'text-bv-mute')}>Billed annually at ${plan.annualTotal.toLocaleString('en-US')}</p>}
                  </div>
                  <ul className="relative mt-6 flex-1 space-y-3">
                    {plan.features.filter(f => f.included).map(feature => (
                      <li key={feature.label} className={cn('flex gap-2.5 text-[15px] leading-6', featured ? 'text-white/80' : 'text-bv-ink2')}>
                        <span className={cn('mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full', featured ? 'bg-bv-success text-white' : 'bg-bv-success-soft text-bv-success')}><Check className="h-2.5 w-2.5" strokeWidth={3} /></span>
                        <span><strong className={featured ? 'text-white' : 'text-bv-ink'}>{feature.label}:</strong> {feature.value}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild variant={featured ? 'default' : 'outline'} className={cn('relative mt-8 h-12 w-full rounded-xl text-[15px] font-semibold', featured ? 'bv-shine' : 'border-bv-blue-line bg-white text-bv-ink hover:bg-bv-blue-wash hover:text-bv-ink')}>
                    <SmartLink href={plan.href}>{plan.cta} <ArrowRight className="h-4 w-4" /></SmartLink>
                  </Button>
                </article>
              </Reveal>
            );
          })}
        </div>
        <Reveal>
          <p className="mx-auto mt-10 max-w-3xl text-center text-[16px] leading-7 text-bv-ink2"><strong className="text-bv-ink">Pricing anchor:</strong> Pro costs $179 a month billed annually — roughly six hours of screening time at a $30 fully loaded hourly cost. A cost comparison, not a savings guarantee. <Link to="/pricing" className="font-semibold text-bv-blue hover:underline">Compare every plan</Link></p>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────────────── FAQ ───────────────────────── */

export function FaqSection() {
  const items = HOME_FAQ_QUESTIONS.map(question => HOME_FAQS.find(item => item.question === question)).filter((item): item is (typeof HOME_FAQS)[number] => Boolean(item));
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="bg-white py-24 lg:py-32" aria-labelledby="faq-title">
      <div className="mx-auto grid max-w-[1240px] gap-12 px-5 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <SectionHeading id="faq-title" eyebrow="FAQ" title="Questions agencies ask before they commit" />
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-3xl border border-bv-blue-line bg-bv-blue-wash/60 p-6">
              <p className="font-display text-lg font-semibold text-bv-ink">Still deciding?</p>
              <p className="mt-2 text-[15px] leading-6 text-bv-ink2">Bring a live vacancy and we’ll walk through where automation ends and recruiter review begins.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button asChild className="h-11 rounded-xl px-5"><Link to="/contact">Book a demo</Link></Button>
                <Button asChild variant="outline" className="h-11 rounded-xl border-bv-blue-line bg-white px-5 text-bv-ink hover:bg-white hover:text-bv-blue"><Link to="/faq">See all questions</Link></Button>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.05}>
          <div className="border-t border-bv-blue-line">
            {items.map((item, i) => {
              const isOpen = open === i;
              return (
                <div key={item.question} className="border-b border-bv-blue-line">
                  <h3>
                    <button type="button" id={`faq-q-${i}`} aria-expanded={isOpen} aria-controls={`faq-a-${i}`} onClick={() => setOpen(isOpen ? null : i)} className="group flex w-full items-center justify-between gap-6 py-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue focus-visible:ring-offset-4">
                      <span className="font-display text-lg font-semibold text-bv-ink transition-colors group-hover:text-bv-blue sm:text-xl">{item.question}</span>
                      <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300', isOpen ? 'rotate-45 border-transparent bg-bv-ink text-white' : 'border-bv-blue-line text-bv-ink')}><Plus className="h-4 w-4" /></span>
                    </button>
                  </h3>
                  <motion.div id={`faq-a-${i}`} role="region" aria-labelledby={`faq-q-${i}`} initial={false} animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }} transition={{ duration: 0.35, ease: EASE }} className="overflow-hidden">
                    <p className="max-w-[680px] pb-6 pr-12 text-[16px] leading-7 text-bv-ink2">{item.answer}</p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────────────── Final CTA ───────────────────────── */

export function FinalCta() {
  return (
    <section className="bg-white px-3 pb-6 pt-4 sm:px-5 lg:pb-10" aria-labelledby="final-cta-title">
      <div className="relative mx-auto max-w-[1280px] overflow-hidden rounded-[36px] bg-bv-ink px-6 py-20 text-center text-white sm:px-12 lg:py-28">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="bv-grid-dark absolute inset-0" />
          <div className="absolute left-1/2 top-[-40%] h-[140%] w-[140%] -translate-x-1/2 animate-spin-slow rounded-full bg-[conic-gradient(from_0deg,transparent_0deg,hsl(var(--hero-highlight-start)/0.22)_60deg,transparent_120deg,hsl(var(--blue)/0.22)_220deg,transparent_300deg)] blur-3xl [animation-duration:24s]" />
          <div className="absolute bottom-[-30%] left-1/2 h-[380px] w-[680px] -translate-x-1/2 rounded-full bg-[hsl(var(--hero-highlight-end)/0.35)] blur-[120px]" />
        </div>
        <div className="relative">
          <Reveal><Eyebrow tone="dark" live>Bring a live vacancy</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 id="final-cta-title" className="mx-auto mt-7 max-w-4xl font-display text-[40px] font-semibold leading-[1.02] tracking-[-0.04em] sm:text-6xl lg:text-7xl">See the agent run <span className="bv-hero-highlight">one of your real roles</span></h2>
          </Reveal>
          <Reveal delay={0.1}><p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/70 sm:text-xl">Bring a live vacancy. We’ll show you where automation ends and recruiter review begins.</p></Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="bv-shine h-[54px] rounded-xl px-8 text-base font-semibold"><a href={APP_URL}>Start with one role <ArrowRight className="h-4 w-4" /></a></Button>
              <Button asChild size="lg" variant="outline" className="h-[54px] rounded-xl border-white/20 bg-white/5 px-8 text-base font-semibold text-white backdrop-blur hover:bg-white/10 hover:text-white"><Link to="/contact">Book a demo</Link></Button>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/60">
              {['Free plan with one active role', 'Runs alongside your ATS', 'Recruiters keep every decision'].map(item => <li key={item} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-bv-success" />{item}</li>)}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
